import {
  ArrowsVertical,
  CaretDown,
  CaretRight,
  Information,
  Pin,
  PinFilled,
  SortAscending,
  SortDescending,
} from '@carbon/react/icons';
import { useSortable } from '@dnd-kit/sortable';
import { flexRender } from '@tanstack/react-table';
import { Button, DefinitionTooltip } from '@carbon/react';
import classNames from 'classnames';
import { DraggableHeaderIcon } from 'components/draggable-icon/draggable-header-icon';
import { ColumnActions } from 'components/table/column-actions';
import styles from './draggable-table-components.module.scss';
import {
  DragCellProps,
  DragHeaderProps,
  draggingStyle,
  getPinningStyles,
} from './utils';

/**
 * Shared draggable table header component with sorting, pinning, and reordering support.
 * Used by tables that implement column drag-and-drop functionality.
 * @return {JSX.Element} The draggable table header element
 */
export const DragHeader = <TData, TValue>({
  header,
  hoveredColumnIdRef,
}: DragHeaderProps<TData, TValue>) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });

  return (
    <th
      ref={setNodeRef}
      key={header.id}
      colSpan={header.colSpan}
      className={classNames(
        header.column.id === hoveredColumnIdRef.current
          ? styles.targetBorderHeader
          : null,
        isDragging ? styles.draggingHeader : null
      )}
      style={{
        ...getPinningStyles(header.column),
        ...(isDragging ? draggingStyle(transform) : {}),
      }}
    >
      {header.isPlaceholder ? null : (
        <div>
          <div
            className={
              header.column.getCanSort() ? styles.labelSortPointer : ''
            }
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
          </div>
          <div className={styles.headerActions}>
            {(() => {
              const { meta } = header.column.columnDef;
              return meta?.infoTooltip ? (
                <DefinitionTooltip
                  className={styles.infoTooltip}
                  align="top-start"
                  definition={meta.infoTooltip}
                  openOnHover
                >
                  <Information />
                </DefinitionTooltip>
              ) : null;
            })()}
            {header.column.getCanSort() && (
              <div
                onClick={header.column.getToggleSortingHandler()}
                className={styles.sortIcon}
              >
                <DefinitionTooltip
                  className={styles.toolTip}
                  align="top"
                  definition="Sort"
                  openOnHover
                >
                  {{
                    asc: <SortAscending />,
                    desc: <SortDescending />,
                  }[header.column.getIsSorted() as string] ?? (
                    <ArrowsVertical />
                  )}
                </DefinitionTooltip>
              </div>
            )}
            <DraggableHeaderIcon
              attributes={attributes}
              listeners={listeners}
            />
            {header.column.getIsPinned() !== 'left' ? (
              <Button
                hasIconOnly
                kind="ghost"
                size="sm"
                iconDescription="Pin Column"
                tooltipAlignment="center"
                tooltipPosition="top"
                renderIcon={Pin}
                onClick={() => {
                  header.column.pin('left');
                }}
              />
            ) : null}
            {header.column.getIsPinned() ? (
              <Button
                hasIconOnly
                kind="ghost"
                size="sm"
                iconDescription="Unpin Column"
                tooltipAlignment="center"
                tooltipPosition="top"
                renderIcon={PinFilled}
                onClick={() => {
                  header.column.pin(false);
                }}
              />
            ) : null}
          </div>
          <ColumnActions handleResize={header.getResizeHandler()} />
        </div>
      )}
    </th>
  );
};

/**
 * Simple draggable table cell component for basic tables without grouping/aggregation.
 * Use DragCellGrouped for tables that support row grouping and aggregation.
 * @return {JSX.Element} The draggable table cell element
 */
export const DragCell = <TData, TValue>({
  cell,
  hoveredColumnIdRef,
}: Omit<DragCellProps<TData, TValue>, 'row'>) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  return (
    <td
      ref={setNodeRef}
      key={cell.id}
      className={classNames(
        cell.column.id === hoveredColumnIdRef.current
          ? styles.targetBorderColumn
          : null,
        isDragging ? styles.draggingColumn : null
      )}
      style={{
        ...getPinningStyles(cell.column),
        ...(isDragging ? draggingStyle(transform) : {}),
      }}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
};

/**
 * Draggable table cell component with support for grouped/aggregated rows.
 * Use this for tables that implement row grouping with expandable groups.
 * @return {JSX.Element} The draggable table cell element with grouping support
 */
export const DragCellGrouped = <TData, TValue>({
  cell,
  row,
  hoveredColumnIdRef,
}: DragCellProps<TData, TValue>) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  return (
    <td
      ref={setNodeRef}
      key={cell.id}
      className={classNames(
        cell.column.id === hoveredColumnIdRef.current
          ? styles.targetBorderColumn
          : null,
        isDragging ? styles.draggingColumn : null
      )}
      style={{
        ...getPinningStyles(cell.column),
        ...(isDragging ? draggingStyle(transform) : {}),
        background: cell.getIsGrouped()
          ? '#defbe6'
          : cell.getIsAggregated()
            ? '#fcf4d6'
            : cell.getIsPlaceholder()
              ? '#f2f4f8'
              : '#ffffff',
      }}
    >
      {cell.getIsGrouped() ? (
        // If it's a grouped cell, add an expander and row count
        <>
          <Button
            size="sm"
            kind="ghost"
            onClick={row.getToggleExpandedHandler()}
            style={{
              cursor: row.getCanExpand() ? 'pointer' : 'normal',
            }}
          >
            {row.getIsExpanded() ? <CaretDown /> : <CaretRight />}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}(
            {row.subRows.length})
          </Button>
        </>
      ) : cell.getIsAggregated() ? (
        // If the cell is aggregated, use the Aggregated renderer for cell
        flexRender(
          cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
          cell.getContext()
        )
      ) : cell.getIsPlaceholder() ? null : (
        // For cells with repeated values, render null
        // Otherwise, just render the regular cell
        flexRender(cell.column.columnDef.cell, cell.getContext())
      )}
    </td>
  );
};
